/**
 * Brand Collection Component
 * Product catalog management with search and grid view
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, Search, Edit, Trash2, Filter, X, ChevronDown, Grid, List, Eye, Heart, ChevronLeft, ChevronRight } from 'lucide-react';
import { useBrand } from '../../context/BrandContext';
import ImageWithFallback from './ImageWithFallback';
import { formatPrice } from '../../services/currencyService';
import '../../assets/css/brand-portal.css';

export function BrandCollection() {
  const navigate = useNavigate();
  const { authFetch, isAuthenticated } = useBrand();
  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 20,
    total: 0,
    totalPages: 0
  });
  const [deleteModal, setDeleteModal] = useState({ show: false, product: null });
  const [deleting, setDeleting] = useState(false);

  const fetchProducts = useCallback(async (page = 1) => {
    if (!isAuthenticated) return;
    
    setLoading(true);
    try {
      const response = await authFetch(`/api/brand/products/?page=${page}&per_page=${pagination.perPage}`);
      const data = await response.json();

      if (response.ok) {
        setProducts(data.products || []);
        setPagination(prev => ({
          ...prev,
          page: data.page,
          total: data.total,
          totalPages: data.total_pages
        }));
      } else {
        setError(data.error || 'Failed to load products');
      }
    } catch (err) {
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, authFetch, pagination.perPage]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleDeleteClick = (product, e) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteModal({ show: true, product });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.product) return;
    
    setDeleting(true);
    try {
      const response = await authFetch(`/api/brand/products/${deleteModal.product.id}/`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setProducts(prev => prev.filter(p => p.id !== deleteModal.product.id));
        setDeleteModal({ show: false, product: null });
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete product');
      }
    } catch (err) {
      setError('Failed to delete product');
    } finally {
      setDeleting(false);
    }
  };

  const filteredProducts = products.filter(product =>
    product.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (product.sku && product.sku.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner} />
        <span style={{ color: '#71717a' }}>Loading products...</span>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="bp-header-row" style={styles.headerRow}>
        <div>
          <div style={styles.label}>C A T A L O G</div>
          <br />
          {/* <h1 style={styles.title}>Collection</h1> */}
          <p style={styles.subtitle}>Manage your collection</p>
        </div>
        <Link to="/brand-portal/products/new" style={styles.newButton}>
          <Plus style={{ width: '18px', height: '18px' }} />
          <span>NEW PRODUCT</span>
        </Link>
      </div>

      {error && (
        <div style={styles.error}>
          <span>{error}</span>
          <button onClick={() => setError('')} style={styles.errorClose}>
            <X style={{ width: '16px', height: '16px' }} />
          </button>
        </div>
      )}

      {/* Search Bar */}
      <div style={styles.searchBar}>
        <Search style={{ width: '20px', height: '20px', color: '#a1a1aa' }} />
        <input
          type="text"
          placeholder="Search by name or category..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      {/* Products Grid */}
      {filteredProducts.length > 0 ? (
        <>
          <div className="bp-collection-grid" style={styles.productsGrid}>
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/brand-portal/products/${product.id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`/brand-portal/products/${product.id}`);
                  }
                }}
                style={{ ...styles.productCard, cursor: 'pointer' }}
              >
                <div style={styles.productImage}>
                  <ImageWithFallback 
                    src={product.image}
                    alt={product.title}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  {/* Edit icon - always visible */}
                  <button 
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate(`/brand-portal/products/${product.id}/edit`);
                    }}
                    style={styles.editIconButton}
                    aria-label="Edit product"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#171717';
                      e.currentTarget.style.color = '#ffffff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.95)';
                      e.currentTarget.style.color = '#171717';
                    }}
                  >
                    <Edit style={{ width: '16px', height: '16px' }} />
                  </button>
                  {/* Delete icon - only for manual products */}
                  {product.is_manual && (
                    <button 
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDeleteClick(product, e);
                      }}
                      style={styles.deleteIconButton}
                      aria-label="Delete product"
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#ef4444';
                        e.currentTarget.style.color = '#ffffff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(255,255,255,0.95)';
                        e.currentTarget.style.color = '#71717a';
                      }}
                    >
                      <Trash2 style={{ width: '16px', height: '16px' }} />
                    </button>
                  )}
                  {/* Out of stock overlay + badge */}
                  {product.is_out_of_stock && (
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'rgba(0,0,0,0.38)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none',
                    }}>
                      <span style={{
                        background: '#171717',
                        color: '#fff',
                        fontSize: '11px',
                        fontWeight: '700',
                        letterSpacing: '1.5px',
                        textTransform: 'uppercase',
                        padding: '6px 14px',
                        borderRadius: '2px',
                      }}>
                        Out of Stock
                      </span>
                    </div>
                  )}
                  {/* Stock status pill */}
                  <span style={{
                    ...styles.stockBadge,
                    background: product.is_out_of_stock ? '#fef2f2' : '#f0fdf4',
                    color: product.is_out_of_stock ? '#ef4444' : '#16a34a',
                    border: `1px solid ${product.is_out_of_stock ? '#fecaca' : '#bbf7d0'}`,
                  }}>
                    {product.is_out_of_stock ? 'Out of Stock' : 'In Stock'}
                  </span>
                </div>
                <div style={styles.productInfo}>
                  <h3 style={styles.productTitle}>{product.title}</h3>
                  {/* <p style={styles.productSku}>
                    {product.sku || `SKU-${product.id.toString().padStart(3, '0')}`}
                  </p> */}
                  <div style={styles.productMeta}>
                    <span style={styles.productPrice}>
                      {formatPrice(product.price, product.currency)}
                    </span>
                    <div style={styles.productStats}>
                      {product.views_count > 0 && (
                        <span style={styles.stat}>
                          <Eye style={{ width: '14px', height: '14px' }} />
                          {product.views_count >= 1000 
                            ? `${(product.views_count / 1000).toFixed(1)}K` 
                            : product.views_count}
                        </span>
                      )}
                      {product.saves_count > 0 && (
                        <span style={{ ...styles.stat, color: '#ef4444' }}>
                          <Heart style={{ width: '14px', height: '14px' }} />
                          {product.saves_count}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div style={styles.pagination}>
              <button
                onClick={() => fetchProducts(pagination.page - 1)}
                disabled={pagination.page === 1}
                style={{
                  ...styles.paginationBtn,
                  opacity: pagination.page === 1 ? 0.5 : 1,
                }}
              >
                <ChevronLeft style={{ width: '16px', height: '16px' }} />
                Previous
              </button>
              <span style={styles.paginationInfo}>
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => fetchProducts(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
                style={{
                  ...styles.paginationBtn,
                  opacity: pagination.page === pagination.totalPages ? 0.5 : 1,
                }}
              >
                Next
                <ChevronRight style={{ width: '16px', height: '16px' }} />
              </button>
            </div>
          )}
        </>
      ) : (
        <div style={styles.emptyState}>
          <Search style={{ width: '48px', height: '48px', color: '#d4d4d4', marginBottom: '16px' }} />
          <h3 style={{ fontSize: '16px', color: '#171717', marginBottom: '8px' }}>
            {searchQuery ? 'No products found' : 'No products yet'}
          </h3>
          <p style={{ fontSize: '14px', color: '#71717a', marginBottom: '24px' }}>
            {searchQuery 
              ? `No products match "${searchQuery}"`
              : 'Start adding products to your collection'}
          </p>
          {!searchQuery && (
            <Link to="/brand-portal/products/new" style={styles.emptyButton}>
              <Plus style={{ width: '18px', height: '18px' }} />
              Add your first product
            </Link>
          )}
        </div>
      )}

      {/* Delete Modal */}
      {deleteModal.show && (
        <div style={styles.modalOverlay} onClick={() => setDeleteModal({ show: false, product: null })}>
          <div className="bp-modal" style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalIcon}>
              <Trash2 style={{ width: '24px', height: '24px', color: '#ef4444' }} />
            </div>
            <h3 style={styles.modalTitle}>Delete Product?</h3>
            <p style={styles.modalText}>
              Are you sure you want to delete "<strong>{deleteModal.product?.title}</strong>"? 
              This action cannot be undone.
            </p>
            <div style={styles.modalActions}>
              <button
                onClick={() => setDeleteModal({ show: false, product: null })}
                style={styles.cancelBtn}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                style={styles.deleteBtn}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '400px',
    gap: '16px',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '2px solid #e5e5e5',
    borderTopColor: '#171717',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '40px',
  },
  label: {
    fontSize: '12px',
    letterSpacing: '0.3em',
    color: '#a1a1aa',
    marginBottom: '8px',
  },
  title: {
    fontSize: '42px',
    fontWeight: '400',
    color: '#171717',
    margin: '0 0 8px 0',
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: '15px',
    color: '#71717a',
    margin: 0,
  },
  newButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '14px 24px',
    background: '#ffffff',
    border: '1px solid #171717',
    borderRadius: '8px',
    color: '#171717',
    fontSize: '13px',
    fontWeight: '600',
    letterSpacing: '0.05em',
    textDecoration: 'none',
    transition: 'all 0.15s',
  },
  error: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#ef4444',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '24px',
    fontSize: '14px',
  },
  errorClose: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#ef4444',
  },
  searchBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px 20px',
    background: '#ffffff',
    border: '1px solid #e5e5e5',
    borderRadius: '12px',
    marginBottom: '32px',
  },
  searchInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: '15px',
    color: '#171717',
    background: 'transparent',
  },
  productsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '24px',
  },
  productCard: {
    textDecoration: 'none',
    color: 'inherit',
  },
  productImage: {
    position: 'relative',
    aspectRatio: '3/4',
    borderRadius: '12px',
    overflow: 'hidden',
    background: '#f5f5f5',
    marginBottom: '16px',
  },
  editIconButton: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    width: '32px',
    height: '32px',
    background: 'rgba(255,255,255,0.95)',
    border: 'none',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#171717',
    transition: 'all 0.2s',
    zIndex: 2,
  },
  deleteIconButton: {
    position: 'absolute',
    top: '12px',
    right: '54px',
    width: '32px',
    height: '32px',
    background: 'rgba(255,255,255,0.95)',
    border: 'none',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#71717a',
    transition: 'color 0.2s, background 0.2s',
    zIndex: 2,
  },
  stockBadge: {
    position: 'absolute',
    bottom: '12px',
    left: '12px',
    padding: '6px 12px',
    background: 'rgba(255,255,255,0.95)',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '500',
    color: '#171717',
  },
  productInfo: {
    padding: '0 4px',
  },
  productTitle: {
    fontSize: '15px',
    fontWeight: '500',
    color: '#171717',
    margin: '0 0 4px 0',
    lineHeight: 1.4,
  },
  productSku: {
    fontSize: '13px',
    color: '#a1a1aa',
    margin: '0 0 12px 0',
  },
  productMeta: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  productPrice: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#171717',
  },
  productStats: {
    display: 'flex',
    gap: '12px',
  },
  stat: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '13px',
    color: '#71717a',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '24px',
    marginTop: '48px',
    paddingTop: '32px',
    borderTop: '1px solid #e5e5e5',
  },
  paginationBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 20px',
    background: '#ffffff',
    border: '1px solid #e5e5e5',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#171717',
    cursor: 'pointer',
  },
  paginationInfo: {
    fontSize: '14px',
    color: '#71717a',
  },
  emptyState: {
    textAlign: 'center',
    padding: '80px 20px',
    background: '#fafafa',
    borderRadius: '12px',
    border: '1px dashed #e5e5e5',
  },
  emptyButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '14px 24px',
    background: '#171717',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '500',
    textDecoration: 'none',
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: '#ffffff',
    borderRadius: '16px',
    padding: '32px',
    maxWidth: '400px',
    width: '100%',
    textAlign: 'center',
  },
  modalIcon: {
    width: '56px',
    height: '56px',
    margin: '0 auto 20px',
    background: '#fef2f2',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#171717',
    margin: '0 0 8px 0',
  },
  modalText: {
    fontSize: '14px',
    color: '#71717a',
    margin: '0 0 24px 0',
    lineHeight: 1.6,
  },
  modalActions: {
    display: 'flex',
    gap: '12px',
  },
  cancelBtn: {
    flex: 1,
    padding: '14px',
    background: '#ffffff',
    border: '1px solid #e5e5e5',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#171717',
    cursor: 'pointer',
  },
  deleteBtn: {
    flex: 1,
    padding: '14px',
    background: '#ef4444',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#ffffff',
    cursor: 'pointer',
  },
};

export default BrandCollection;
